<script setup lang="ts">
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

const props = defineProps<{
  tripId: number
  tripTitle?: string | null
}>()

const emit = defineEmits<{
  close: []
  created: []
}>()

const { t } = useI18n()
const { createRewindVideo } = useTripService()
const { trackEvent } = useAnalyticsTrackingService()
const mainStore = useMainStore()
const formatMap: Record<string, { label: string; desc: string }> = {
  portrait: {
    label: t('rewind_settings_orientation_portrait_title'),
    desc: t('rewind_settings_orientation_portrait_description')
  },
  landscape: {
    label: t('rewind_settings_orientation_landscape_title'),
    desc: t('rewind_settings_orientation_landscape_description')
  }
}

const colorOptions: Record<string, { hex: string; swatchClass: string }> = {
  gray: { hex: '#4a4a4a', swatchClass: 'tw-bg-gray-700' },
  blue: { hex: '#3e8dba', swatchClass: 'tw-bg-riser-blue' },
  yellow: { hex: '#ffe200', swatchClass: 'tw-bg-riser-yellow' }
}

const colorKeys = Object.keys(colorOptions)

const state = reactive({
  orientation: 'portrait' as 'portrait' | 'landscape',
  showTitle: false,
  showPhotos: false,
  title: props.tripTitle,
  selectedColor: 'blue',
  loading: false
})

const resolvedColor = computed(() => {
  return colorOptions[state.selectedColor]?.hex ?? colorOptions[colorKeys[0]].hex
})

async function submit() {
  state.loading = true

  try {
    const unit = mainStore.settings?.distance as 'metric' | 'imperial' | undefined

    await createRewindVideo(props.tripId, {
      orientation: state.orientation,
      lineColor: resolvedColor.value,
      showPhotos: state.showPhotos,
      showTitle: state.showTitle,
      title: state.title ?? undefined,
      unit
    })

    trackEvent(
      ANALYTICS_EVENTS.tripRewindCreate({
        orientation: state.orientation,
        lineColor: resolvedColor.value,
        unit
      })
    )

    emit('created')
    emit('close')
  } catch (error) {
    console.error('Error creating rewind video', error)
  } finally {
    state.loading = false
  }
}

onMounted(() => {
  trackEvent(ANALYTICS_EVENTS.tripRewindSettingShow())
})
</script>

<template>
  <Transition name="modal">
    <Modal footer-classes="tw-p-4" hide-header @close="$emit('close')">
      <template #body>
        <div class="tw-mb-4 tw-text-center">
          <h2 class="tw-text-2xl tw-font-bold">{{ t('rewind_settings_header_title') }}</h2>
        </div>
        <p class="tw-mb-6 tw-text-center tw-text-sm tw-opacity-70">
          {{ t('rewind_settings_intro') }}
        </p>

        <InputSelectCore
          v-model="state.orientation"
          :options-keys="Object.keys(formatMap)"
          class="tw-mb-4 tw-flex tw-flex-col tw-gap-3"
        >
          <template #item="{ currentKey, isSelected, onClick }">
            <div
              class="tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-rounded-xl tw-border-2 tw-p-4 tw-transition-all"
              :class="isSelected ? 'tw-border-gray-700' : 'tw-border-gray-300'"
              @click="onClick"
            >
              <div
                class="tw-flex tw-h-14 tw-w-14 tw-flex-shrink-0 tw-items-center tw-justify-center"
              >
                <div
                  class="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-gray-200"
                  :class="currentKey === 'portrait' ? 'tw-h-14 tw-w-10' : 'tw-h-10 tw-w-14'"
                >
                  <FontAwesomeIcon icon="play" class="tw-text-gray-500" />
                </div>
              </div>

              <div>
                <p class="tw-font-bold">{{ formatMap[currentKey]?.label }}</p>
                <p class="tw-text-xs tw-opacity-60">{{ formatMap[currentKey]?.desc }}</p>
              </div>
            </div>
          </template>
        </InputSelectCore>

        <InputCheckboxCore v-model="state.showTitle" class="tw-mb-4">
          <template #default="{ isChecked, onClick }">
            <div
              class="tw-flex tw-cursor-pointer tw-items-center tw-justify-between"
              @click="onClick"
            >
              <span class="tw-text-sm tw-font-medium">{{ t('rewind_settings_show_title') }}</span>
              <div
                class="tw-relative tw-h-6 tw-w-11 tw-rounded-full tw-transition-colors"
                :class="isChecked ? 'tw-bg-riser-green' : 'tw-bg-gray-400'"
              >
                <div
                  class="tw-absolute tw-left-0.5 tw-top-0.5 tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform"
                  :class="isChecked ? 'tw-translate-x-5' : 'tw-translate-x-0'"
                />
              </div>
            </div>
          </template>
        </InputCheckboxCore>

        <div v-if="state.showTitle" class="tw-mb-4">
          <input
            v-model="state.title"
            type="text"
            class="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm"
            :placeholder="t('Title')"
          />
        </div>

        <InputCheckboxCore v-model="state.showPhotos" class="tw-mb-4">
          <template #default="{ isChecked, onClick }">
            <div
              class="tw-flex tw-cursor-pointer tw-items-center tw-justify-between"
              @click="onClick"
            >
              <span class="tw-text-sm tw-font-medium">{{
                t('rewind_settings_show_photos')
              }}</span>
              <div
                class="tw-relative tw-h-6 tw-w-11 tw-rounded-full tw-transition-colors"
                :class="isChecked ? 'tw-bg-riser-green' : 'tw-bg-gray-400'"
              >
                <div
                  class="tw-absolute tw-left-0.5 tw-top-0.5 tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform"
                  :class="isChecked ? 'tw-translate-x-5' : 'tw-translate-x-0'"
                />
              </div>
            </div>
          </template>
        </InputCheckboxCore>

        <div class="tw-mb-2 tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-sm tw-font-medium">{{ t('rewind_settings_path_color') }}</span>
          <InputSelectCore
            v-model="state.selectedColor"
            :options-keys="colorKeys"
            class="tw-flex tw-gap-3"
          >
            <template #item="{ currentKey, isSelected, onClick }">
              <div
                class="tw-h-10 tw-w-10 tw-cursor-pointer tw-rounded-full tw-border-2 tw-transition-all"
                :class="[
                  isSelected
                    ? 'tw-border-white tw-ring-2 tw-ring-gray-800'
                    : 'tw-border-transparent',
                  colorOptions[currentKey]?.swatchClass
                ]"
                @click="onClick"
              />
            </template>
          </InputSelectCore>
        </div>
      </template>

      <template #footer>
        <InputAppButton
          :loading="state.loading"
          class="tw-w-full tw-rounded-full tw-bg-riser-blue tw-px-4 tw-py-3 tw-font-bold tw-text-white"
          @click="submit"
        >
          {{ t('rewind_settings_create_video') }}
        </InputAppButton>
      </template>
    </Modal>
  </Transition>
</template>
